import { useCallback, useMemo } from 'react';
import { fetchCards, deleteCard, deleteCards } from '../api/api';
import { useEntityList } from './UseEntityList';

// ─── useCards ─────────────────────────────────────────────────────────────────
// UseCardData.ts(393줄, useCards/useInlinePriceEdit/useCardJobs 세 관심사가 한 파일)를
// 파일별로 나눈 것 중 하나(2026-08-11, 로직 변경 없는 기계적 이동) — useCardData()가
// 이 세 훅을 조합한다.

export function useCards() {
  // 이 훅은 CardPageProvider 하나를 여러 화면(가격 조회·스토어 상품 관리·상품 등록·반영
  // 로그·매입 등)이 공유한다 — 재조회 실패 토스트를 여기서 전역으로 끄면 인라인 배너를
  // 그리지 않는 화면의 실패가 조용히 묻힌다. 토스트 억제는 배너를 그리는 화면이 마운트
  // 동안만 `setInlineError`(useInlineLoadError)로 켠다(2026-10-21 회귀 수정).
  const {
    items: cards, setItems: setCards, loading, reload, replaceItem: replaceCard, mergeResults,
    error, retry, hasLoadedOnce, setInlineError, loadVersion,
  } = useEntityList(fetchCards, '카드 목록을 불러오지 못했습니다.');

  const removeCard = useCallback(async (id: number) => {
    await deleteCard(id);
    setCards((prev) => prev.filter((c) => c.id !== id));
  }, [setCards]);

  const removeCards = useCallback(async (ids: number[]) => {
    const { deleted } = await deleteCards(ids);
    setCards((prev) => prev.filter((c) => !ids.includes(c.id)));
    return deleted;
  }, [setCards]);

  // Context value로 그대로 나가는 객체라 useMemo로 감싼다 — 그래야 cards/loading이
  // 실제로 안 바뀐 렌더에서는 참조가 유지되어, 이 값을 구독하는 하위 트리(카드 테이블 등)가
  // 무관한 상태 변화(모달 토글 등)만으로 매번 리렌더되지 않는다.
  return useMemo(
    () => ({
      cards, setCards, loading, reload, replaceCard, mergeResults, removeCard, removeCards,
      error, retry, hasLoadedOnce, setInlineError, loadVersion,
    }),
    [
      cards, setCards, loading, reload, replaceCard, mergeResults, removeCard, removeCards,
      error, retry, hasLoadedOnce, setInlineError, loadVersion,
    ],
  );
}
